import { useTranslation } from 'react-i18next';
import type { ApprovalChatSuggestion } from '@/types/approvalChat';
import type { SuggestionState } from '@/utils/approval/approvalChat';
import {
  SuggestionAsk,
  SuggestionButton,
  SuggestionButtons,
  SuggestionCard as Card,
  SuggestionComment,
  SuggestionNote,
  SuggestionResolved,
} from './styles';

type Props = {
  suggestion: ApprovalChatSuggestion;
  state: SuggestionState;
  /** 현재 결재 차례인 사용자만 버튼을 본다. 그 외에는 질문만 보여 준다. */
  canDecide: boolean;
  /** 이 화면에서 방금 응답한 제안 — 결과 말풍선이 뒤따르므로 카드를 숨긴다. 이력 복원된 응답은 카드에 결과 한 줄 */
  resolvedQuietly?: boolean;
  submitting: boolean;
  /** 같은 답변의 전달을 먼저 처리한다는 안내 등 — 버튼 위 한 줄 */
  note?: string;
  onAccept: () => void;
  onDecline: () => void;
};

/**
 * 답변에 딸린 결재 처리 제안 카드. 「네, 반려 처리」/「네, 승인 처리」는 기존 reject / confirm API 를
 * 그대로 부른다 — 결재 차례·상태 검증은 서버가 다시 하므로 여기서는 노출 조건만 거른다.
 */
const SuggestionCard = ({
  suggestion,
  state,
  canDecide,
  resolvedQuietly = false,
  submitting,
  note,
  onAccept,
  onDecline,
}: Props) => {
  const { t } = useTranslation();

  const isApprove = suggestion.action === 'approve';
  const hasComment = suggestion.comment.trim() !== '';

  if (state !== 'open') {
    if (resolvedQuietly) return null;
    const accepted = state === 'accepted';
    const resolvedText = accepted
      ? isApprove
        ? t('승인 처리됨')
        : t('반려 처리됨')
      : t('제안을 적용하지 않았습니다');
    return (
      <Card>
        <SuggestionResolved accepted={accepted}>
          {accepted ? '✓ ' : ''}
          {resolvedText}
        </SuggestionResolved>
      </Card>
    );
  }

  const fallbackAsk = isApprove
    ? hasComment
      ? t('위 보완 요청을 코멘트로 입력하고 승인 처리할까요?')
      : t('검토 결과 특이사항이 없습니다. 승인 처리할까요?')
    : t('위 확인 사항을 반려 코멘트로 입력하고 반려 처리할까요?');

  return (
    <Card>
      <SuggestionAsk>{suggestion.ask ?? fallbackAsk}</SuggestionAsk>
      {/* 「네」를 누르면 결재 의견으로 그대로 들어갈 문구 — 채팅으로 직접 지시한 코멘트도 누르기 전에 확인하게 (PORTAL-15726) */}
      <SuggestionComment>
        <b>{t('입력할 코멘트')}</b>
        {hasComment ? suggestion.comment.trim() : t('없음 (의견 없이 승인)')}
      </SuggestionComment>
      {canDecide && note && <SuggestionNote>{note}</SuggestionNote>}
      {canDecide && (
        <SuggestionButtons>
          <SuggestionButton
            type="button"
            primary
            disabled={submitting}
            onClick={onAccept}
          >
            {isApprove ? t('네, 승인 처리') : t('네, 반려 처리')}
          </SuggestionButton>
          <SuggestionButton
            type="button"
            disabled={submitting}
            onClick={onDecline}
          >
            {t('아니요')}
          </SuggestionButton>
        </SuggestionButtons>
      )}
    </Card>
  );
};

export default SuggestionCard;
